import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import '../../../../styles/artifactory.css';   // NOT globally loaded — required for the flip CSS
import { artifactoryService } from '../../../../services/artifactoryService';
import { useTheme } from '../../../../contexts/ThemeContext';
import { useConversation } from '../../../../contexts/ConversationContext';
import { councilWsClient } from '../../../../services/councilWsClient';
import { deriveBlueprintSummary, BlueprintCardSummary } from '../utils/blueprintSummary';
import { useChatDagRuntime } from '../../../../contexts/ChatDagRuntimeContext';
import type { BlueprintNodeData } from '../types';

// SP2b — poll cadence + TTL for the germinating→bloom watch. 30 ticks × 3s ≈
// 90s before we surface "taking longer" (genesis is a heavy architect call).
const GENESIS_POLL_MS = 3000;
const GENESIS_MAX_TICKS = 30;
// How many of the most-recent landed paths the Growing face shows (newest at bottom).
const GROWING_TAIL = 6;

interface LiveStatus {
  germinating?: boolean;
  genesis_failed?: boolean;
  node_count?: number;
  expected_count?: number | null;   // rough N/~T target while germinating (A8)
}

interface LandedNode {
  index: number;
  path: string;
}

const BlueprintNode: React.FC<NodeProps<BlueprintNodeData>> = ({ data, id }) => {
  const { darkMode } = useTheme();
  const sessionId = useConversation().state?.id ?? null;
  const runtime = useChatDagRuntime();
  const spineExpanded = runtime.expandedCardSpineIds.has(id);
  const [summary, setSummary] = useState<BlueprintCardSummary | null>(null);
  const [flipped, setFlipped] = useState(false);
  // Live genesis-poll state — overrides the one-shot manifest rollup while it runs.
  const [live, setLive] = useState<LiveStatus | null>(null);
  const [justBloomed, setJustBloomed] = useState(false);
  const [overtime, setOvertime] = useState(false);
  const [cacheOpen, setCacheOpen] = useState(false);   // SP-3c Slice B — in-card cache disclosure
  const prevGerminatingRef = useRef(false);
  // Live node-by-node growth (WS-driven, AUTHORITATIVE for the growth faces — the status
  // poll can't be relied on to flip germinating in time on the live path: the card mounts as
  // a seedling and genesis finishes in ~10-25s, often before any refetch). `landed` upserts
  // by index (small models/retries re-emit); `completedRef` latches on genesis_complete so a
  // LATE node_landed can't repopulate; `composing` = genesis_started seen, no nodes yet.
  const [landed, setLanded] = useState<LandedNode[]>([]);
  const [composing, setComposing] = useState(false);
  const completedRef = useRef(false);   // latches on genesis_complete (drops late node_landed)
  const landedRef = useRef(0);
  landedRef.current = landed.length;    // read current count inside effects without re-running them

  // Reset growth state whenever the card points at a different manifest.
  useEffect(() => {
    setLanded([]);
    setComposing(false);
    completedRef.current = false;
  }, [data.manifestId]);

  // One-shot rollup fetch (re-callable on bloom to repopulate after genesis).
  const loadManifest = useCallback(() => {
    let alive = true;
    artifactoryService.getManifest(data.manifestId)
      .then((m) => { if (alive) setSummary(deriveBlueprintSummary(m)); })
      .catch(() => { if (alive) setSummary(null); });
    return () => { alive = false; };
  }, [data.manifestId]);
  useEffect(() => loadManifest(), [loadManifest]);

  // Effective lifecycle: the live poll wins once it's running; else the rollup.
  const germinating = live ? !!live.germinating : !!summary?.germinating;
  const genesisFailed = live ? !!live.genesis_failed : !!summary?.genesisFailed;
  const nodeCount = live?.node_count ?? summary?.nodeCount ?? 0;
  const seedling = !!summary?.seedling;
  // SP-2 — a seedling card defaults its intent spine EXPANDED so the "shape the
  // intent below" CTA isn't orphaned. Seeds IN the card (not from chat messages,
  // per the plan-triad fold): the seedling face is async manifest state, so the
  // effect waits for `summary` to resolve. A ref latches it to once-per-mount
  // (so a later manual collapse sticks); only adds, never collapses.
  const spineSeededRef = useRef(false);
  useEffect(() => {
    if (spineSeededRef.current || !seedling) return;
    spineSeededRef.current = true;
    runtime.seedCardSpineExpanded(id);
  }, [seedling, id, runtime]);
  // SP-3a — F5 survival: a card the operator has already replied on
  // (hasOperatorTurns, stamped by the processor in Task 5) defaults its intent
  // spine EXPANDED on mount so the conversation isn't hidden behind a collapsed
  // card after a reload. Idempotent set-ADD via the SAME seeder (a card that is
  // BOTH seedling and operator-touched can't flip-flop to net-collapsed); a
  // later manual collapse sticks (ref latches once-per-mount).
  const operatorSeededRef = useRef(false);
  useEffect(() => {
    if (operatorSeededRef.current || !data?.hasOperatorTurns) return;
    operatorSeededRef.current = true;
    runtime.seedCardSpineExpanded(id);
  }, [data?.hasOperatorTurns, id, runtime]);
  // WS-confirmed growth wins over the poll-derived germinating. `genesisActive` gates the
  // status poll so it runs during a live stream (to fetch expected_count + observe the
  // committed-done state for bloom) even before/without germinating ever reading true.
  const growing = landed.length > 0;
  const genesisActive = germinating || composing || growing;

  // Poll the SESSION-OWNED status endpoint (decision 2 — NOT GET /manifest) while genesis
  // is active. setTimeout recursion, alive-flag cleanup, TTL via ticks guard.
  useEffect(() => {
    if (!sessionId || !genesisActive) return;
    let alive = true;
    let ticks = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      if (!alive) return;
      if (ticks < GENESIS_MAX_TICKS) {
        ticks += 1;
      } else {
        setOvertime(true);
        return;   // stop re-arming once over the TTL; the card shows "taking longer"
      }
      try {
        const s = await artifactoryService.blueprintStatus(sessionId);
        if (!alive) return;
        setLive(s);
        if (s.germinating) timer = setTimeout(tick, GENESIS_POLL_MS);
        // a non-germinating result ends the loop; the bloom/failure effect reacts.
      } catch {
        if (alive) timer = setTimeout(tick, GENESIS_POLL_MS);
      }
    };
    timer = setTimeout(tick, GENESIS_POLL_MS);
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [sessionId, genesisActive]);

  // Live genesis growth. The per-session WS bus (connection_manager) broadcasts genesis_*
  // events to every socket on the session — including the council `/events` channel that
  // councilWsClient subscribes to (no AuthContext/import.meta in its import graph, unlike
  // useWebSocket, so the chatdag barrel stays jest-loadable). Every handler is FILTERED by
  // manifest_id so a sibling card's stream never leaks in.
  useEffect(() => {
    if (!sessionId) return;
    const unsub = councilWsClient.subscribe(sessionId, (raw: any) => {
      const d = raw?.data;
      if (!d || d.manifest_id !== data.manifestId) return;   // not our blueprint
      switch (raw.type) {
        case 'genesis_started':
          if (!completedRef.current) setComposing(true);     // lead-in, kicks the status poll
          break;
        case 'genesis_node_landed':
          if (completedRef.current) return;                  // latch: ignore late nodes
          setLanded((prev) => {
            const i = prev.findIndex((n) => n.index === d.index);
            if (i !== -1) {                                  // dedupe by index (retry)
              const next = prev.slice();
              next[i] = { index: d.index, path: d.path };
              return next;
            }
            return [...prev, { index: d.index, path: d.path }];
          });
          break;
        case 'genesis_complete':
          completedRef.current = true;
          // Nudge a refetch — harmless if it races the bg commit (poll still drives bloom).
          loadManifest();
          break;
        default:
          break;
      }
    });
    return unsub;
  }, [sessionId, data.manifestId, loadManifest]);

  // Bloom: genesis done + nodes present. Fires when we either (a) saw germinating go true→false
  // (poll/fixture path) OR (b) grew nodes live over WS and the manifest is now committed
  // (the live path, where germinating may never have read true on the card). Clears the live
  // growth state once + refetches so the rollup owns the bloomed face.
  useEffect(() => {
    const done = !germinating && nodeCount > 0;
    if (done && (prevGerminatingRef.current || landedRef.current > 0)) {
      setJustBloomed(true);
      setOvertime(false);
      setLanded([]);
      setComposing(false);
      completedRef.current = false;
      setLive(null);
      loadManifest();
    }
    prevGerminatingRef.current = germinating;
  }, [germinating, nodeCount, loadManifest]);
  useEffect(() => {
    if (!justBloomed) return;
    const t = setTimeout(() => setJustBloomed(false), 1600);
    return () => clearTimeout(t);
  }, [justBloomed]);

  const flip = useCallback((e: React.MouseEvent) => { e.stopPropagation(); setFlipped((f) => !f); }, []);
  const open = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    data.onOpen?.(data.manifestId);
  }, [data]);
  const generate = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    data.onGenerate?.(data.manifestId, summary?.rootContext ?? '');
  }, [data, summary]);

  const shell = darkMode
    ? 'bg-gray-800 border-amber-500/40 text-gray-100'
    : 'bg-amber-50 border-amber-400 text-gray-800';
  const faceBg = darkMode ? 'bg-gray-800' : 'bg-amber-50';
  const subtle = darkMode ? 'text-gray-400' : 'text-gray-500';
  const chip = darkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700 border border-amber-200';

  // ── Genesis lifecycle faces (shown instead of the flip card) ────────────────
  if (genesisFailed) {
    return (
      <div
        data-testid="blueprint-genesis-failed"
        className={`card-container w-full rounded-lg border-2 shadow-lg overflow-hidden ${shell}`}
        style={{ width: 420 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Handle type="target" position={Position.Left} id="l" style={{ opacity: 0 }} />
        <Handle type="source" position={Position.Bottom} id="b" style={{ opacity: 0 }} />
        <div className={`p-5 flex flex-col gap-3 ${faceBg}`}>
          <div className="flex items-center gap-2">
            <span className="text-2xl" aria-hidden>🥀</span>
            <div className="text-base font-bold text-rose-400">Genesis failed</div>
          </div>
          <div className={`text-sm ${subtle}`}>
            {summary?.genesisError || 'The architect could not generate this blueprint.'}
          </div>
          <div className="flex justify-end">
            <button
              data-testid="blueprint-genesis-retry"
              onClick={generate}
              disabled={germinating}
              className="text-xs font-medium px-3 py-1 rounded bg-amber-500 text-gray-900 hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ↻ Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (genesisActive) {
    const expected = live?.expected_count ?? null;
    const grown = landed.length;
    const counter = expected ? `${grown}/~${expected}` : `${grown}`;
    const tail = landed.slice(-GROWING_TAIL);

    // GROWING — files are landing one-by-one. Fixed-height list (newest at bottom, fade
    // mask) so the ReactFlow node box never changes size mid-stream (§11-C2).
    if (grown > 0) {
      return (
        <div
          data-testid="blueprint-growing"
          className={`card-container w-full rounded-lg border-2 shadow-lg overflow-hidden ${shell}`}
          style={{ width: 420 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Handle type="target" position={Position.Left} id="l" style={{ opacity: 0 }} />
          <Handle type="source" position={Position.Bottom} id="b" style={{ opacity: 0 }} />
          <div className={`p-5 flex flex-col gap-3 ${faceBg}`}>
            <div className="flex items-center gap-2">
              <span className="text-3xl blueprint-germinating-pulse" aria-hidden>🌱</span>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-emerald-500 font-semibold">Growing</div>
                <div className="text-base font-bold">
                  Growing… <span className={`font-normal ${subtle}`} data-testid="blueprint-growing-counter">({counter})</span>
                </div>
              </div>
            </div>
            <div
              data-testid="blueprint-growing-list"
              className="relative h-[132px] overflow-hidden"
              style={{
                WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, black 28px)',
                maskImage: 'linear-gradient(to bottom, transparent 0, black 28px)',
              }}
            >
              <div className="absolute bottom-0 left-0 right-0 flex flex-col gap-1">
                {tail.map((n) => (
                  <div
                    key={n.index}
                    className={`blueprint-landed-row truncate font-mono text-[11px] px-2 py-1 rounded ${chip}`}
                  >
                    <span className="text-emerald-500">+</span> {n.path}
                  </div>
                ))}
              </div>
            </div>
            {overtime && (
              <div className="text-xs text-amber-400">⏳ Taking longer than expected — still working…</div>
            )}
          </div>
        </div>
      );
    }

    // COMPOSING — genesis started (or a fresh reconnect lead-in) but no nodes have landed
    // yet. Honest lead-in (NOT a bare pulse) even when reconnecting mid-stream (§11-C4).
    return (
      <div
        data-testid="blueprint-composing"
        className={`card-container w-full rounded-lg border-2 shadow-lg overflow-hidden ${shell}`}
        style={{ width: 420 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Handle type="target" position={Position.Left} id="l" style={{ opacity: 0 }} />
        <Handle type="source" position={Position.Bottom} id="b" style={{ opacity: 0 }} />
        <div className={`p-5 flex flex-col gap-3 items-center text-center ${faceBg}`}>
          <span className="text-3xl blueprint-germinating-pulse" aria-hidden>🌱</span>
          <div className="text-base font-bold">
            Composing…{expected ? ` (0/~${expected})` : ''}
          </div>
          <div className={`text-sm ${subtle}`}>
            The foreman is drafting your blueprint. Files appear here as they land.
          </div>
          {overtime && (
            <div className="text-xs text-amber-400">⏳ Taking longer than expected — still working…</div>
          )}
        </div>
      </div>
    );
  }

  if (seedling && nodeCount === 0) {
    return (
      <div
        data-testid="blueprint-seedling"
        className={`card-container w-full rounded-lg border-2 shadow-lg overflow-hidden ${shell}`}
        style={{ width: 420 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Handle type="target" position={Position.Left} id="l" style={{ opacity: 0 }} />
        <Handle type="source" position={Position.Bottom} id="b" style={{ opacity: 0 }} />
        <div className={`p-5 flex flex-col gap-3 ${faceBg}`}>
          <div className="flex items-center gap-2">
            <span className="text-2xl" aria-hidden>🌱</span>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-emerald-500 font-semibold">New Blueprint</div>
              <div className="text-base font-bold truncate">{summary?.name ?? 'Seedling'}</div>
            </div>
          </div>
          <div className={`text-sm ${subtle}`}>
            Shape the intent on the spine below, then grow the full scaffold.
          </div>
          <div className="flex justify-end">
            <button
              data-testid="blueprint-generate"
              onClick={generate}
              disabled={germinating}
              className="text-xs font-medium px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ✨ Generate
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="blueprint-bloomed"
      className={`card-container w-full rounded-lg border-2 shadow-lg overflow-hidden ${shell} ${flipped ? 'card-flipped' : ''} ${justBloomed ? 'blueprint-bloom' : ''}`}
      style={{ width: 420 }}
      onClick={(e) => e.stopPropagation()}
    >
      <Handle type="target" position={Position.Left} id="l" style={{ opacity: 0 }} />
      {/* SP2a — the card ROOTS its intent spine hanging below; source handle 'b'
          anchors the synthetic card→intent-root fork edge. */}
      <Handle type="source" position={Position.Bottom} id="b" style={{ opacity: 0 }} />
      <div className="card-inner">
        {/* FRONT — summary */}
        <div className={`card-front flex flex-col ${faceBg}`}>
          <div className="px-4 py-3 border-b border-amber-500/30 flex items-center gap-2">
            <span className="text-2xl" aria-hidden>📐</span>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-amber-500 font-semibold">Living Blueprint</div>
              <div className="text-base font-bold truncate">{summary?.name ?? 'Loading…'}</div>
            </div>
          </div>

          <div className="flex-1 p-4 flex flex-col gap-3 min-h-0 overflow-y-auto custom-scrollbar">
            <div className="flex flex-wrap gap-2">
              <span className={`px-2 py-1 rounded text-xs font-medium ${chip}`}>
                🟢 {summary?.freshCount ?? 0} fresh
              </span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${chip}`}>
                🟡 {summary?.staleCount ?? 0} stale
              </span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${chip}`}>
                👻 {summary?.ghostCount ?? 0} ghost
              </span>
            </div>
            <div className={`text-sm ${subtle}`}>
              {summary?.nodeCount ?? 0} node{(summary?.nodeCount ?? 0) === 1 ? '' : 's'} in this blueprint
            </div>

            {/* SP-3c Slice B — in-card "cache" disclosure: the blueprint's materialized
                (non-ghost) files, default-collapsed. Driven by the existing getManifest
                fetch (summary.files); the blueprint owns its files via its manifest, so
                these are NOT loose orphan tiles (suppressed from the tray in Task 3). */}
            {(summary?.files?.length ?? 0) > 0 && (
              <div className="flex flex-col">
                <button
                  data-testid="blueprint-cache-toggle"
                  onClick={(e) => { e.stopPropagation(); setCacheOpen((o) => !o); }}
                  aria-expanded={cacheOpen}
                  aria-label={cacheOpen ? 'Hide produced files' : 'Show produced files'}
                  className={`self-start text-xs font-medium px-2 py-1 rounded flex items-center gap-1 transition-colors ${
                    darkMode ? 'text-amber-300 hover:bg-gray-700' : 'text-amber-700 hover:bg-amber-100'
                  }`}
                >
                  ▣ {summary?.files?.length ?? 0} cache <span aria-hidden>{cacheOpen ? '▾' : '▸'}</span>
                </button>
                {cacheOpen && (
                  <div
                    data-testid="blueprint-cache-list"
                    className="mt-1 max-h-[140px] overflow-y-auto custom-scrollbar flex flex-col gap-1"
                  >
                    {(summary?.files ?? []).map((p) => (
                      <div key={p} className={`truncate font-mono text-[11px] px-2 py-1 rounded ${chip}`}>
                        {p}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-4 py-2 border-t border-amber-500/30 flex items-center justify-between">
            {/* SP-2 — disclosure chevron for the intent spine that hangs below
                this card (toggles expandedCardSpineIds; the layout hides/shows
                the lane + drops a dormant stem when collapsed). */}
            <button
              data-testid="blueprint-spine-toggle"
              onClick={(e) => { e.stopPropagation(); runtime.toggleCardSpineExpansion(id); }}
              aria-expanded={spineExpanded}
              aria-label={spineExpanded ? 'Collapse intent spine' : 'Expand intent spine'}
              className={`text-xs font-medium px-2 py-1 rounded flex items-center gap-1 transition-colors ${
                darkMode ? 'text-amber-300 hover:bg-gray-700' : 'text-amber-700 hover:bg-amber-100'
              }`}
            >
              <span aria-hidden>{spineExpanded ? '⌄' : '▸'}</span> Intent
            </button>
            <button
              data-testid="blueprint-flip"
              onClick={flip}
              className="text-xs font-medium px-3 py-1 rounded bg-amber-500 text-gray-900 hover:bg-amber-400 transition-colors"
            >
              Intent changelog →
            </button>
          </div>
        </div>

        {/* BACK — intent changelog */}
        <div className={`card-back flex flex-col ${faceBg}`} onClick={(e) => e.stopPropagation()}>
          <div className="px-4 py-3 border-b border-amber-500/30 flex items-center justify-between">
            <div className="text-sm font-bold">Intent changelog</div>
            <button
              onClick={flip}
              className={`text-xs ${subtle} hover:underline`}
            >
              ← back
            </button>
          </div>

          <div className="flex-1 p-4 overflow-y-auto custom-scrollbar flex flex-col gap-3">
            {(summary?.changelog ?? []).length === 0 && (
              <div className={`text-sm ${subtle}`}>No intent changes yet.</div>
            )}
            {(summary?.changelog ?? []).map((e, i) => (
              <div key={e.id} className={`rounded p-2 ${chip}`}>
                <div className="text-xs font-medium leading-snug">{e.content}</div>
                {e.mutation_reason && (
                  <div className={`text-[11px] mt-1 italic ${subtle}`}>{e.mutation_reason}</div>
                )}
                <div className={`text-[10px] mt-1 ${subtle}`}>
                  {i === 0 ? 'current' : `v-${i}`} · {e.affected_nodes?.length ?? 0} node{(e.affected_nodes?.length ?? 0) === 1 ? '' : 's'} affected
                </div>
              </div>
            ))}
          </div>

          <div className="px-4 py-2 border-t border-amber-500/30 flex justify-end">
            <button
              onClick={open}
              className="text-xs font-medium px-3 py-1 rounded bg-amber-500 text-gray-900 hover:bg-amber-400 transition-colors"
            >
              Open in Artifactory
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlueprintNode;
